import { SubmitHandler, useForm } from "react-hook-form";
import { useState } from "react";
import { addSponsor } from "../../../../../store/sponsor/actions/add";
import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  IconButton,
  Image,
  Input,
  Radio,
  RadioGroup,
  SimpleGrid,
  Text,
  Wrap,
} from "@chakra-ui/react";
import { toggleSponsorVisibility } from "../../../../../store/sponsor/actions/toggleVisibility";
import { DeleteIcon } from "@chakra-ui/icons";
import { deleteSponsor } from "../../../../../store/sponsor/actions/delete";
import { TalesSpinner } from "../../../../atoms";
import { FileUploader } from "../../../../atoms/FileUploader";
import { useDispatch, useSelector } from "react-redux";
import { AppThunkDispatch } from "../../../../../store";
import {
  getSponsorData,
  getSponsorError,
  isSponsorLoading,
} from "../../../../../store/sponsor/selectors";

enum RoleEnum {
  isPatrocinio = "isPatrocinio",
  isSponsor = "isSponsor",
  isCollaborator = "isCollaborator",
  isOrganizer = "isOrganizer",
}

interface SponsorInput {
  role: RoleEnum;
  name: string;
  show: boolean;
}
export default function Sponsors() {
  const dispatch = useDispatch<AppThunkDispatch>();
  const isPending = useSelector(isSponsorLoading);
  const sponsors = useSelector(getSponsorData);
  const error = useSelector(getSponsorError);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    getValues,
  } = useForm<SponsorInput>({
    defaultValues: {
      role: RoleEnum.isSponsor,
      name: "",
      show: true,
    },
  });
  const [file, setFile] = useState<File>();

  const onSubmit: SubmitHandler<SponsorInput> = (data) => {
    const newSponsor = {
      name: getValues("name"),
      isCollaborator: getValues("role") === "isCollaborator",
      isOrganizer: getValues("role") === "isOrganizer",
      isPatrocinio: getValues("role") === "isPatrocinio",
      isSponsor: getValues("role") === "isSponsor",
      show: getValues("show"),
      file: file,
    };
    dispatch(addSponsor(newSponsor));
  };

  return (
    <>
      {error && (
        <Box>
          <Text my="10px">{error}</Text>
        </Box>
      )}
      <Wrap>
        {!isPending &&
          sponsors &&
          sponsors.map((sponsor) => (
            <Card key={sponsor.logoUrl} w="xs" m="10px">
              <CardHeader>
                <Heading size="base">{sponsor.name}</Heading>
                {sponsor.isSponsor && <Text>Sponsor</Text>}
                {sponsor.isPatrocinio && <Text>Patrocinio</Text>}
                {sponsor.isCollaborator && <Text>Collaboratore</Text>}
                {sponsor.isOrganizer && <Text>Organizzatore</Text>}
              </CardHeader>
              <CardBody>
                <Image src={sponsor.logoUrl} maxH="80px" alt={sponsor.name} />
              </CardBody>
              <CardFooter>
                <Button
                  aria-label={sponsor.show ? "Nascondi" : "Mostra"}
                  variant="solid"
                  colorScheme={sponsor.show ? "gray" : "green"}
                  onClick={() => dispatch(toggleSponsorVisibility(sponsor))}
                  marginRight="20px"
                >
                  {sponsor.show ? "NASCONDI DAL SITO" : "MOSTRA SUL SITO"}
                </Button>
                <IconButton
                  aria-label="Elimina"
                  variant="solid"
                  icon={<DeleteIcon />}
                  colorScheme="red"
                  onClick={() => dispatch(deleteSponsor(sponsor))}
                />
              </CardFooter>
            </Card>
          ))}
        <Card w="xs" m="10px">
          {isPending && (
            <CardBody>
              <Box w="30px" m="auto">
                <TalesSpinner />
              </Box>
            </CardBody>
          )}
          {!isPending && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardHeader>
                <Heading size="base">Aggiungi nuovo</Heading>
              </CardHeader>
              <CardBody>
                <FormControl
                  marginBottom="20px"
                  isInvalid={errors.name != null}
                  isRequired={true}
                >
                  <FormLabel htmlFor="name">Nome</FormLabel>
                  <Input
                    id="name"
                    type="text"
                    {...register("name", { required: "Campo richiesto" })}
                  />
                  <FormErrorMessage>
                    {errors.name && errors.name.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isRequired={true} marginBottom="20px">
                  <FormLabel>Categoria:</FormLabel>
                  <RadioGroup defaultValue={getValues("role")}>
                    <SimpleGrid
                      columns={2}
                      spacing="10px"
                      minChildWidth="100px"
                    >
                      <Radio value="isSponsor" {...register("role")}>
                        Sponsor
                      </Radio>
                      <Radio value="isPatrocinio" {...register("role")}>
                        Patrocinio
                      </Radio>
                      <Radio value="isCollaborator" {...register("role")}>
                        Collaboratore
                      </Radio>
                      <Radio value="isOrganizer" {...register("role")}>
                        Organizzatore
                      </Radio>
                    </SimpleGrid>
                  </RadioGroup>
                </FormControl>
                <FormControl isRequired={true} marginBottom="20px">
                  <FormLabel htmlFor={"file"}>Logo</FormLabel>
                  <FileUploader handleFile={(file) => setFile(file)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Mostrare sul sito</FormLabel>
                  <RadioGroup defaultValue={getValues("show").toString()}>
                    <Flex>
                      <Radio
                        value="true"
                        marginRight="30px"
                        {...register("show")}
                      >
                        Sì
                      </Radio>
                      <Radio value="false" {...register("show")}>
                        No
                      </Radio>
                    </Flex>
                  </RadioGroup>
                </FormControl>
              </CardBody>
              <CardFooter>
                <Button
                  type="submit"
                  isDisabled={
                    isPending ||
                    watch("name") === "" ||
                    !file ||
                    !watch("name") ||
                    sponsors?.some((sponsor) => sponsor.name === watch("name"))
                  }
                  colorScheme="green"
                >
                  Crea
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>
      </Wrap>
    </>
  );
}
